import type { SchoolRole } from "./roles";

/**
 * What people in a school may do, as a catalogue of permissions. A role (built in or made by
 * the school) is a set of grants: a permission at a scope, written "student.view:school".
 * Each person's grants travel in their access token, and the Cerbos policies for school
 * records are generated from this file (`bun run policy:generate`), so the catalogue, the
 * policies and the role editor can't drift apart. See docs/student-onboarding-and-access.md §5.
 */

/**
 * How far a grant reaches. Staff scopes nest: the whole school includes the sections someone
 * teaches or is class teacher of, which include the sections they are class teacher of.
 * Family scopes stand alone and belong to the built-in Parent and Student roles.
 */
export const PERMISSION_SCOPES = [
  "school",
  "my_sections",
  "class_sections",
  "own_children",
  "self",
] as const;

export type PermissionScope = (typeof PERMISSION_SCOPES)[number];

export const STAFF_SCOPES = [
  "school",
  "my_sections",
  "class_sections",
] as const satisfies readonly PermissionScope[];

export type StaffScope = (typeof STAFF_SCOPES)[number];

export const SCOPE_LABELS: Record<PermissionScope, string> = {
  school: "Whole school",
  my_sections: "Sections they teach",
  class_sections: "Their class sections",
  own_children: "Their own children",
  self: "Their own record",
};

export const SCOPE_HINTS: Record<PermissionScope, string> = {
  school: "Every class and section.",
  my_sections: "Sections they are class teacher of or teach a subject in.",
  class_sections: "Only the sections they are class teacher of.",
  own_children: "Only their own children's records.",
  self: "Only their own record.",
};

/**
 * Wider staff scopes cover narrower ones, and the whole school covers everything in it
 * (someone's own children too); other family scopes cover only themselves.
 */
const STAFF_RANK: Record<StaffScope, number> = { school: 3, my_sections: 2, class_sections: 1 };

export function isStaffScope(scope: PermissionScope): scope is StaffScope {
  return (STAFF_SCOPES as readonly string[]).includes(scope);
}

export function scopeCovers(held: PermissionScope, wanted: PermissionScope): boolean {
  if (held === "school") return true;
  if (isStaffScope(held) && isStaffScope(wanted)) return STAFF_RANK[held] >= STAFF_RANK[wanted];
  return held === wanted;
}

export const PERMISSION_MODULES = ["students", "academics", "people"] as const;
export type PermissionModule = (typeof PERMISSION_MODULES)[number];

export const PERMISSION_MODULE_LABELS: Record<PermissionModule, string> = {
  students: "Students",
  academics: "Classes and sections",
  people: "Staff and roles",
};

/** Cerbos resource kinds whose policies are generated from this catalogue. */
export type SchoolResourceKind =
  | "student"
  | "section"
  | "school_class"
  | "academic_year"
  | "school_user"
  | "school_role"
  | "admission_form";

export interface PermissionDef {
  id: string;
  module: PermissionModule;
  label: string;
  hint: string;
  /** The Cerbos resources and actions this permission allows (some only at some scopes). */
  targets: readonly {
    resource: SchoolResourceKind;
    actions: readonly string[];
    scopes?: readonly PermissionScope[];
  }[];
  /** Scopes it may be granted at, widest first. */
  scopes: readonly PermissionScope[];
  /** Extra condition for some scopes, in Cerbos CEL (e.g. only students still studying). */
  conditions?: Partial<Record<PermissionScope, { expr: string; note: string }>>;
  /** Added automatically with this one. */
  requires: readonly string[];
  /**
   * Controls who can do what (accounts, passwords, roles). Giving it to anyone needs
   * `role.grant_privileged`, and Eduleo's support team can't reset the password of anyone who
   * holds it.
   */
  privileged: boolean;
  /** Personal data beyond names and classes; shown with a note in the role editor. */
  sensitive: boolean;
}

const STUDENT_SCOPES = ["school", "my_sections", "class_sections", "own_children", "self"] as const;

export const PERMISSIONS = [
  {
    id: "student.view",
    module: "students",
    label: "See students",
    hint: "Names, classes, admission numbers and dates of birth.",
    targets: [
      { resource: "student", actions: ["view"] },
      // The school's own record (staff); families see what the school shows parents.
      { resource: "student", actions: ["view_internal"], scopes: STAFF_SCOPES },
    ],
    scopes: STUDENT_SCOPES,
    requires: [],
    privileged: false,
    sensitive: false,
  },
  {
    id: "student.view_contact",
    module: "students",
    label: "See parents' phone numbers",
    hint: "The full number instead of only the last four digits.",
    targets: [{ resource: "student", actions: ["view_contact"] }],
    scopes: STUDENT_SCOPES,
    requires: ["student.view"],
    privileged: false,
    sensitive: true,
  },
  {
    id: "student.create",
    module: "students",
    label: "Add students",
    hint: "Admit new students into any class.",
    targets: [{ resource: "student", actions: ["create"] }],
    scopes: ["school"],
    requires: ["student.view"],
    privileged: false,
    sensitive: false,
  },
  {
    id: "student.update",
    module: "students",
    label: "Change student details",
    hint: "Edit names, classes and parents' phone numbers.",
    targets: [{ resource: "student", actions: ["update"] }],
    scopes: ["school", "my_sections", "class_sections"],
    conditions: {
      my_sections: {
        expr: 'R.attr.status == "active"',
        note: "Only students who are still studying.",
      },
      class_sections: {
        expr: 'R.attr.status == "active"',
        note: "Only students who are still studying.",
      },
    },
    requires: ["student.view"],
    privileged: false,
    sensitive: false,
  },
  {
    id: "student.delete",
    module: "students",
    label: "Remove students",
    hint: "Take a student's record out of the school's lists.",
    targets: [{ resource: "student", actions: ["delete"] }],
    scopes: ["school"],
    requires: ["student.view"],
    privileged: false,
    sensitive: false,
  },
  {
    id: "student.export",
    module: "students",
    label: "Download student lists",
    hint: "A CSV of the students they can see: names, classes, dates of birth and status. Never parents' numbers. Every download is recorded.",
    targets: [{ resource: "student", actions: ["export"] }],
    scopes: ["school"],
    requires: ["student.view"],
    privileged: false,
    sensitive: true,
  },
  {
    id: "student.view_sensitive",
    module: "students",
    label: "See private details",
    hint: "Religion, category, caste, family income and the school's private questions.",
    targets: [{ resource: "student", actions: ["view_sensitive"] }],
    scopes: ["school", "own_children"],
    requires: ["student.view"],
    privileged: false,
    sensitive: true,
  },
  {
    id: "student.view_health",
    module: "students",
    label: "See health details",
    hint: "Health conditions, allergies, medicines, special needs and the family doctor.",
    targets: [{ resource: "student", actions: ["view_health"] }],
    scopes: ["school", "my_sections", "class_sections", "own_children"],
    requires: ["student.view"],
    privileged: false,
    sensitive: true,
  },
  {
    id: "student.documents",
    module: "students",
    label: "See and add documents",
    hint: "Open students' documents (birth certificate, TC, ID proofs…) and upload new ones.",
    targets: [{ resource: "student", actions: ["view_documents", "upload_document"] }],
    scopes: STUDENT_SCOPES,
    requires: ["student.view"],
    privileged: false,
    sensitive: true,
  },
  {
    id: "student.verify",
    module: "students",
    label: "Check documents",
    hint: "Mark documents verified or rejected. Families see the result.",
    targets: [{ resource: "student", actions: ["verify"] }],
    scopes: ["school"],
    requires: ["student.documents"],
    privileged: false,
    sensitive: false,
  },
  {
    id: "student.portal",
    module: "students",
    label: "Family portal",
    hint: "Give parents and students their sign-in, and approve what they send: their first details and later changes.",
    targets: [{ resource: "student", actions: ["manage_portal"] }],
    scopes: ["school"],
    requires: ["student.view", "student.view_contact"],
    privileged: true,
    sensitive: false,
  },
  {
    id: "student.request_change",
    module: "students",
    label: "Ask for changes",
    hint: "Families ask the school to change details or add contacts; the school approves each change.",
    targets: [
      { resource: "student", actions: ["request_change"] },
      // Adding contacts and sending the record to the school are a parent's, not the student's.
      { resource: "student", actions: ["act_for_family"], scopes: ["own_children"] },
    ],
    scopes: ["own_children", "self"],
    requires: ["student.view"],
    privileged: false,
    sensitive: false,
  },
  {
    id: "admission.settings",
    module: "students",
    label: "Set up the admission form",
    hint: "Choose the details and documents asked at admission, add the school's own questions, and the admission number format.",
    targets: [{ resource: "admission_form", actions: ["view", "update"] }],
    scopes: ["school"],
    requires: [],
    privileged: false,
    sensitive: false,
  },
  {
    id: "academics.view",
    module: "academics",
    label: "See classes and sections",
    hint: "The class list, sections, class teachers and academic years.",
    targets: [
      { resource: "school_class", actions: ["view"] },
      { resource: "section", actions: ["view"] },
      { resource: "academic_year", actions: ["view"] },
    ],
    scopes: ["school"],
    requires: [],
    privileged: false,
    sensitive: false,
  },
  {
    id: "academics.manage",
    module: "academics",
    label: "Set up classes and sections",
    hint: "Add and change classes, sections, class teachers and academic years.",
    targets: [
      { resource: "school_class", actions: ["create", "update"] },
      { resource: "section", actions: ["create", "update", "delete"] },
      { resource: "academic_year", actions: ["create", "update"] },
    ],
    scopes: ["school"],
    requires: ["academics.view"],
    privileged: false,
    sensitive: false,
  },
  {
    id: "seating.manage",
    module: "academics",
    label: "Design seating plans",
    hint: "Set a section's rows, columns and desks. Class teachers can be given this for their own sections.",
    targets: [{ resource: "section", actions: ["update_seating"] }],
    scopes: ["school", "my_sections", "class_sections"],
    requires: ["academics.view"],
    privileged: false,
    sensitive: false,
  },
  {
    id: "seating.template",
    module: "academics",
    label: "Copy seating plans and set class defaults",
    hint: "Copy one section's plan to other sections, and choose the plan a class's new sections start with.",
    targets: [
      { resource: "section", actions: ["copy_seating"] },
      { resource: "school_class", actions: ["update_seating"] },
    ],
    scopes: ["school"],
    requires: ["seating.manage"],
    privileged: false,
    sensitive: false,
  },
  {
    id: "user.view",
    module: "people",
    label: "See staff accounts",
    hint: "Names, User IDs, roles and whether each account is on.",
    targets: [{ resource: "school_user", actions: ["view"] }],
    scopes: ["school"],
    requires: ["role.view"],
    privileged: false,
    sensitive: false,
  },
  {
    id: "user.export",
    module: "people",
    label: "Download the staff list",
    hint: "A CSV of names, User IDs, designations, emails, roles and status. Every download is recorded.",
    targets: [{ resource: "school_user", actions: ["export"] }],
    scopes: ["school"],
    requires: ["user.view"],
    privileged: false,
    sensitive: true,
  },
  {
    id: "user.manage",
    module: "people",
    label: "Add and change staff accounts",
    hint: "Add staff, change their details and roles, and turn accounts off.",
    targets: [{ resource: "school_user", actions: ["create", "update"] }],
    scopes: ["school"],
    requires: ["user.view"],
    privileged: true,
    sensitive: false,
  },
  {
    id: "user.reset_password",
    module: "people",
    label: "Reset staff passwords",
    hint: "Set a temporary password or email a link to choose one.",
    targets: [{ resource: "school_user", actions: ["reset_password"] }],
    scopes: ["school"],
    requires: ["user.view"],
    privileged: true,
    sensitive: false,
  },
  {
    id: "role.view",
    module: "people",
    label: "See roles",
    hint: "The school's roles and what each one allows.",
    targets: [{ resource: "school_role", actions: ["view"] }],
    scopes: ["school"],
    requires: [],
    privileged: false,
    sensitive: false,
  },
  {
    id: "role.manage",
    module: "people",
    label: "Create and change roles",
    hint: "Make the school's own roles from the permissions they hold themselves.",
    targets: [{ resource: "school_role", actions: ["create", "update"] }],
    scopes: ["school"],
    requires: ["role.view"],
    privileged: true,
    sensitive: false,
  },
  {
    id: "role.grant_privileged",
    module: "people",
    label: "Give out access permissions",
    hint: "Needed to give anyone the permissions marked “Access”: accounts, passwords and roles.",
    targets: [{ resource: "school_role", actions: ["grant_privileged"] }],
    scopes: ["school"],
    requires: ["role.manage"],
    privileged: true,
    sensitive: false,
  },
] as const satisfies readonly PermissionDef[];

export type PermissionId = (typeof PERMISSIONS)[number]["id"];

const BY_ID = new Map<string, PermissionDef>(PERMISSIONS.map((entry) => [entry.id, entry]));

export function permissionDef(id: string): PermissionDef | undefined {
  return BY_ID.get(id);
}

export function isPermissionId(value: unknown): value is PermissionId {
  return typeof value === "string" && BY_ID.has(value);
}

export interface Grant {
  permission: PermissionId;
  scope: PermissionScope;
}

/** "student.view:school": the form grants take in tokens and Cerbos policies. */
export function grantKey(grant: Grant): string {
  return `${grant.permission}:${grant.scope}`;
}

export function parseGrantKey(key: string): Grant | null {
  const [permission, scope, extra] = key.split(":");
  if (extra !== undefined || !isPermissionId(permission)) return null;
  const def = BY_ID.get(permission);
  if (!def || !(def.scopes as readonly string[]).includes(scope ?? "")) return null;
  return { permission, scope: scope as PermissionScope };
}

/** True when `held` includes `wanted`: the same permission at the same or a wider scope. */
export function grantsCover(held: readonly Grant[], wanted: Grant): boolean {
  return held.some(
    (grant) => grant.permission === wanted.permission && scopeCovers(grant.scope, wanted.scope),
  );
}

/**
 * One list from several roles' grants: per permission, the widest staff scope, plus any
 * family scopes (a teacher who is also a parent at the school keeps both). Sorted, so the
 * same access always gives the same token.
 */
export function mergeGrants(lists: readonly (readonly Grant[])[]): Grant[] {
  const staff = new Map<PermissionId, Grant>();
  const family = new Map<string, Grant>();
  for (const grant of lists.flat()) {
    if (isStaffScope(grant.scope)) {
      const current = staff.get(grant.permission);
      if (!current || scopeCovers(grant.scope, current.scope)) {
        staff.set(grant.permission, grant);
      }
    } else {
      family.set(grantKey(grant), grant);
    }
  }
  // Code-point order, not the locale's: the same on every machine.
  return [...staff.values(), ...family.values()].sort((a, b) =>
    grantKey(a) < grantKey(b) ? -1 : 1,
  );
}

/** Adds what each grant requires, at the same scope where allowed, else the narrowest one. */
export function withRequirements(grants: readonly Grant[]): Grant[] {
  const result = new Map<string, Grant>();
  const visit = (grant: Grant) => {
    const existing = result.get(grant.permission);
    if (existing && scopeCovers(existing.scope, grant.scope)) return;
    result.set(grant.permission, grant);
    const def = BY_ID.get(grant.permission);
    for (const required of def?.requires ?? []) {
      const requiredDef = BY_ID.get(required);
      if (!requiredDef) continue;
      const scope = (requiredDef.scopes as readonly PermissionScope[]).includes(grant.scope)
        ? grant.scope
        : (requiredDef.scopes.find((candidate) => scopeCovers(candidate, grant.scope)) ??
          requiredDef.scopes[0]);
      if (scope) visit({ permission: required as PermissionId, scope });
    }
  };
  for (const grant of grants) visit(grant);
  return mergeGrants([[...result.values()]]);
}

export function isPrivilegedGrant(grant: Grant): boolean {
  return BY_ID.get(grant.permission)?.privileged === true;
}

// ---------------------------------------------------------------------------------------------
// Built-in roles
// ---------------------------------------------------------------------------------------------

const grant = (permission: PermissionId, scope: PermissionScope = "school"): Grant => ({
  permission,
  scope,
});

/** Permissions a staff role can hold (the rest are for families only). */
export const STAFF_PERMISSIONS: readonly PermissionDef[] = (
  PERMISSIONS as readonly PermissionDef[]
).filter((entry) => entry.scopes.some(isStaffScope));

const ALL_STAFF_GRANTS: Grant[] = STAFF_PERMISSIONS.map((entry) => grant(entry.id as PermissionId));

/**
 * What each built-in role allows. Schools can't change these; they copy one into a role of
 * their own instead. Built-in roles are stored by key in `user_roles`.
 */
export const ROLE_TEMPLATES: Record<SchoolRole, { description: string; grants: Grant[] }> = {
  institution_admin: {
    description: "Everything in the school, including staff accounts and roles.",
    grants: ALL_STAFF_GRANTS,
  },
  principal: {
    description: "Every student and class. Can see staff and roles but not change them.",
    grants: [
      grant("student.view"),
      grant("student.view_contact"),
      grant("student.create"),
      grant("student.update"),
      grant("student.delete"),
      grant("student.export"),
      grant("student.view_sensitive"),
      grant("student.view_health"),
      grant("student.documents"),
      grant("student.verify"),
      grant("student.portal"),
      grant("admission.settings"),
      grant("academics.view"),
      grant("academics.manage"),
      grant("seating.manage"),
      grant("seating.template"),
      grant("user.view"),
      grant("user.export"),
      grant("role.view"),
    ],
  },
  teacher: {
    description:
      "Students in the sections they teach. Parents' numbers and changes only as class teacher.",
    grants: [
      grant("student.view", "my_sections"),
      grant("student.view_contact", "class_sections"),
      grant("student.update", "class_sections"),
      grant("academics.view"),
    ],
  },
  accountant: {
    description: "Every student's name and class, for fees. No parents' numbers.",
    grants: [grant("student.view"), grant("academics.view")],
  },
  parent: {
    description:
      "Their own children's records, documents and private details. Changes go to the school for approval.",
    grants: [
      grant("student.view", "own_children"),
      grant("student.view_contact", "own_children"),
      grant("student.view_sensitive", "own_children"),
      grant("student.view_health", "own_children"),
      grant("student.documents", "own_children"),
      grant("student.request_change", "own_children"),
    ],
  },
  student: {
    description: "Their own record and documents. Changes go to the school for approval.",
    grants: [
      grant("student.view", "self"),
      grant("student.view_contact", "self"),
      grant("student.documents", "self"),
      grant("student.request_change", "self"),
    ],
  },
};

/** Built-in roles a school gives its staff. Parent and student accounts come from records. */
export const STAFF_SYSTEM_ROLES = [
  "institution_admin",
  "principal",
  "teacher",
  "accountant",
] as const satisfies readonly SchoolRole[];

export type StaffSystemRole = (typeof STAFF_SYSTEM_ROLES)[number];

export function isStaffSystemRole(value: unknown): value is StaffSystemRole {
  return typeof value === "string" && (STAFF_SYSTEM_ROLES as readonly string[]).includes(value);
}

/** Grants of built-in roles; platform roles and unknown values add nothing. */
export function templateGrants(roles: readonly string[]): Grant[] {
  return mergeGrants(
    roles.map((role) =>
      Object.hasOwn(ROLE_TEMPLATES, role) ? ROLE_TEMPLATES[role as SchoolRole].grants : [],
    ),
  );
}

/** Starting points for a school's own roles, besides copying a built-in one. */
export const ROLE_PRESETS = [
  {
    key: "front_office",
    name: "Front office",
    description: "Admissions and student records, with parents' numbers.",
    grants: [
      grant("student.view"),
      grant("student.view_contact"),
      grant("student.create"),
      grant("student.update"),
      grant("academics.view"),
    ],
  },
  {
    key: "data_entry",
    name: "Data entry",
    description: "Adds and updates student records. No parents' numbers.",
    grants: [
      grant("student.view"),
      grant("student.create"),
      grant("student.update"),
      grant("academics.view"),
    ],
  },
  {
    key: "view_only",
    name: "View only",
    description: "Sees students and classes, changes nothing.",
    grants: [grant("student.view"), grant("academics.view")],
  },
] as const satisfies readonly {
  key: string;
  name: string;
  description: string;
  grants: readonly Grant[];
}[];
